import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Checkbox,
    LinearProgress,
    Stack,
    Switch,
    Typography
} from '@mui/material';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AdmicityStatus from '../../../shared-components/AdmicityStatus';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import AdmicityTableV2 from '../../../shared-components/Table/V2/AdmicityTableV2';
import {useGetYearGroupsQuery} from '../../../api/services/groupService';
import {usePreviewStudentParentInvitationsMutation} from '../../../api/services/schoolManagementService';
import useNotification from '../../../utility/hooks/useNotification';
import {
    PARENT_INVITATION_TARGET_MODES
} from '../../../constants/parentInvitationTargetModes';

/* eslint-disable react/prop-types */

const defaultSettings = {
    disableParentInvitations: true,
    parentInvitationTargetMode: PARENT_INVITATION_TARGET_MODES.selectedYearGroups,
    parentInvitationYearGroupIds: []
};

const normaliseSettings = (value) => ({
    ...defaultSettings,
    ...(value ?? {}),
    parentInvitationTargetMode: Number(value?.parentInvitationTargetMode ?? defaultSettings.parentInvitationTargetMode),
    parentInvitationYearGroupIds: value?.parentInvitationYearGroupIds ?? []
});

const isSelectableYearGroup = (yearGroup) =>
    yearGroup.name?.trim().toLowerCase() !== 'year group';

const resolveSettings = (value, yearGroups) => {
    const settings = normaliseSettings(value);
    const selectableYearGroupIds = new Set(yearGroups.map(yearGroup => yearGroup.id));

    if (settings.disableParentInvitations) {
        return {
            ...settings,
            parentInvitationTargetMode: PARENT_INVITATION_TARGET_MODES.selectedYearGroups,
            parentInvitationYearGroupIds: []
        };
    }

    if (settings.parentInvitationTargetMode === PARENT_INVITATION_TARGET_MODES.allEligibleContacts) {
        return {
            ...settings,
            parentInvitationTargetMode: PARENT_INVITATION_TARGET_MODES.selectedYearGroups,
            parentInvitationYearGroupIds: yearGroups.map(yearGroup => yearGroup.id)
        };
    }

    return {
        ...settings,
        parentInvitationYearGroupIds: yearGroups.length > 0
            ? settings.parentInvitationYearGroupIds.filter(id => selectableYearGroupIds.has(id))
            : settings.parentInvitationYearGroupIds
    };
};

const getSummaryLabel = (settings, yearGroupCount) => {
    if (settings.disableParentInvitations) {
        return '';
    }

    if (yearGroupCount > 0 && settings.parentInvitationYearGroupIds.length === yearGroupCount) {
        return 'All year groups selected';
    }

    return `${settings.parentInvitationYearGroupIds.length} year group${
        settings.parentInvitationYearGroupIds.length === 1 ? '' : 's'
    } selected`;
};

const areSettingsEqual = (left, right) =>
    left.disableParentInvitations === right.disableParentInvitations &&
    left.parentInvitationTargetMode === right.parentInvitationTargetMode &&
    left.parentInvitationYearGroupIds.length === right.parentInvitationYearGroupIds.length &&
    left.parentInvitationYearGroupIds.every((id, index) => id === right.parentInvitationYearGroupIds[index]);

const StudentParentInvitationsSettings = ({schoolId, value, onChange, embedded = false}) => {
    const [open, setOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [draft, setDraft] = useState(normaliseSettings(value));
    const [preview, setPreview] = useState({items: [], totalCount: 0, wouldSendCount: 0});
    const {data: yearGroups = [], isFetching: isFetchingYearGroups} = useGetYearGroupsQuery();
    const selectableYearGroups = useMemo(() => yearGroups.filter(isSelectableYearGroup), [yearGroups]);
    const [previewStudentParentInvitations, {isLoading: isPreviewLoading}] = usePreviewStudentParentInvitationsMutation();
    const {showErrorNotification} = useNotification();
    const resolvedValueSettings = useMemo(() => resolveSettings(value, selectableYearGroups), [value, selectableYearGroups]);
    const [appliedSettings, setAppliedSettings] = useState(resolvedValueSettings);
    const [hasPendingAppliedSettings, setHasPendingAppliedSettings] = useState(false);
    const settings = hasPendingAppliedSettings ? appliedSettings : resolvedValueSettings;

    useEffect(() => {
        if (hasPendingAppliedSettings) {
            if (areSettingsEqual(resolvedValueSettings, appliedSettings)) {
                setHasPendingAppliedSettings(false);
            }

            return;
        }

        setAppliedSettings(resolvedValueSettings);
    }, [resolvedValueSettings, appliedSettings, hasPendingAppliedSettings]);

    useEffect(() => {
        if (open) {
            setDraft(settings);
        }
    }, [open, settings]);

    const selectedYearGroupIds = useMemo(
        () => new Set(draft.parentInvitationYearGroupIds),
        [draft.parentInvitationYearGroupIds]
    );
    const yearGroupRows = useMemo(
        () => selectableYearGroups.map(yearGroup => ({...yearGroup, id: yearGroup.id})),
        [selectableYearGroups]
    );
    const previewRows = useMemo(
        () => preview.items.map(item => ({...item, id: `${item.studentId}_${item.contactId}`})),
        [preview.items]
    );

    const setYearGroupSelection = (yearGroupId, selected) => {
        setDraft(previous => {
            const current = new Set(previous.parentInvitationYearGroupIds);

            if (selected) {
                current.add(yearGroupId);
            } else {
                current.delete(yearGroupId);
            }

            return {
                ...previous,
                disableParentInvitations: selected ? false : current.size === 0,
                parentInvitationTargetMode: PARENT_INVITATION_TARGET_MODES.selectedYearGroups,
                parentInvitationYearGroupIds: [...current]
            };
        });
    };

    const setAllYearGroupsSelection = (selected) => {
        setDraft(previous => ({
            ...previous,
            disableParentInvitations: !selected,
            parentInvitationTargetMode: PARENT_INVITATION_TARGET_MODES.selectedYearGroups,
            parentInvitationYearGroupIds: selected ? selectableYearGroups.map(yearGroup => yearGroup.id) : []
        }));
    };

    const handlePreview = async () => {
        try {
            const result = await previewStudentParentInvitations({
                schoolId,
                ...draft
            }).unwrap();
            setPreview(result);
            setPreviewOpen(true);
        } catch (e) {
            showErrorNotification('Failed to preview student parent invitations');
        }
    };

    const applyChanges = () => {
        setAppliedSettings(draft);
        setHasPendingAppliedSettings(true);
        onChange(draft);
        setOpen(false);
    };

    const yearGroupColumns = useMemo(() => [
        {
            accessorKey: 'selected',
            header: '',
            size: 70,
            Cell: ({row}) => (
                <Checkbox
                    checked={selectedYearGroupIds.has(row.original.id)}
                    onChange={(event) => setYearGroupSelection(row.original.id, event.target.checked)}
                />
            )
        },
        {
            accessorKey: 'name',
            header: 'Year Group',
            Cell: ({row}) => row.original.name
        }
    ], [selectedYearGroupIds]);

    const previewColumns = useMemo(() => [
        {
            accessorKey: 'studentName',
            header: 'Student'
        },
        {
            accessorKey: 'yearGroupName',
            header: 'Year Group'
        },
        {
            accessorKey: 'contactName',
            header: 'Contact'
        },
        {
            accessorKey: 'contactEmail',
            header: 'Email'
        },
        {
            accessorKey: 'wouldSend',
            header: 'Current Eligibility',
            size: 150,
            Cell: ({row}) => (
                <AdmicityStatus
                    label={row.original.wouldSend ? 'Eligible' : 'Not eligible'}
                    color={row.original.wouldSend ? 'green' : 'grey'}
                />
            )
        },
        {
            accessorKey: 'reason',
            header: 'Note'
        }
    ], []);

    return (
        <Box
            sx={{
                border: embedded ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: embedded ? 0 : 1,
                p: embedded ? 0 : 2,
                mt: embedded ? 0 : 2,
                mb: embedded ? 0 : 1
            }}
        >
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} justifyContent="space-between">
                <Box>
                    <Typography fontWeight={600}>Disable Student Parent Invitations</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {getSummaryLabel(settings, selectableYearGroups.length)}
                    </Typography>
                </Box>
                <Button
                    type="button"
                    variant="outlined"
                    startIcon={<ManageAccountsRoundedIcon/>}
                    onClick={() => setOpen(true)}
                >
                    Manage
                </Button>
            </Stack>

            <AdmicityDialog
                open={open}
                handleClose={() => setOpen(false)}
                title="Disable Student Parent Invitations"
                maxWidth="md"
                actions={[
                    {
                        label: 'Cancel',
                        onClick: () => setOpen(false)
                    },
                    {
                        label: 'Apply',
                        onClick: applyChanges
                    }
                ]}
            >
                <Stack spacing={2} sx={{pt: 1, minHeight: 420}}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography fontWeight={600}>Disable Student Parent Invitations</Typography>
                            </Box>
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                <Switch
                                    checked={draft.disableParentInvitations}
                                    onChange={(event) => setAllYearGroupsSelection(!event.target.checked)}
                                />
                            </Box>
                        </Stack>

                        {isFetchingYearGroups && <LinearProgress/>}
                        <AdmicityTableV2
                            emptyStateMinHeight={120}
                            noRowsOverlay={{text: 'No year groups available.'}}
                            columns={yearGroupColumns}
                            data={yearGroupRows}
                            initialState={{
                                density: 'compact',
                                columnFilters: [],
                                expanded: {}
                            }}
                            tableProps={{
                                enableSorting: false,
                                enableFilters: false,
                                enableHiding: false,
                                enableFullScreenToggle: false,
                                enableColumnResizing: false,
                                enableBottomToolbar: false,
                                enableTopToolbar: false,
                                enablePagination: false,
                                enableStickyHeader: true,
                                muiTablePaperProps: {
                                    variant: 'outlined',
                                    elevation: 0,
                                    sx: {
                                        display: 'block',
                                        flexGrow: 0,
                                        width: '100%',
                                        height: 'auto'
                                    }
                                },
                                muiTableContainerProps: {
                                    sx: {
                                        flex: 'unset',
                                        minHeight: 200,
                                        maxHeight: 360,
                                        overflow: 'auto'
                                    }
                                },
                                muiTableBodyCellProps: {
                                    sx: {
                                        py: 0.25
                                    }
                                },
                                muiTableHeadCellProps: {
                                    sx: {
                                        py: 0.5
                                    }
                                }
                            }}
                        />

                        <Box>
                            <Button
                                type="button"
                                variant="outlined"
                                startIcon={<VisibilityRoundedIcon/>}
                                disabled={isPreviewLoading}
                                onClick={handlePreview}
                            >
                                Preview recipients
                            </Button>
                            {isPreviewLoading && <LinearProgress sx={{mt: 1}}/>}
                        </Box>
                </Stack>
            </AdmicityDialog>

            <AdmicityDialog
                open={previewOpen}
                handleClose={() => setPreviewOpen(false)}
                title={
                    <>
                    Preview recipients
                    <Typography variant="body2" color="text.secondary">
                        {preview.wouldSendCount} of {preview.totalCount} contacts would receive an invitation, or account will be linked
                    </Typography>
                    </>
                }
                maxWidth="lg"
                contentSx={{overflowY: 'auto', overflowX: 'hidden'}}
                actions={[{label: 'Close', onClick: () => setPreviewOpen(false)}]}
            >
                <Box sx={{maxWidth: '100%'}}>
                    <AdmicityTableV2
                        emptyStateMinHeight={120}
                        noRowsOverlay={{text: 'No recipients match the selected year groups.'}}
                        columns={previewColumns}
                        data={previewRows}
                        tableProps={{
                            enableSorting: false,
                            enableFilters: false,
                            enableHiding: false,
                            enableFullScreenToggle: false,
                            enableColumnResizing: false,
                            enableBottomToolbar: previewRows.length > 10,
                            enableTopToolbar: false,
                            enablePagination: previewRows.length > 10,
                            enableStickyHeader: true,
                            muiTablePaperProps: {
                                variant: 'outlined',
                                elevation: 0,
                                sx: {
                                    display: 'block',
                                    flexGrow: 0,
                                    width: '100%',
                                    height: 'auto'
                                }
                            },
                            muiTableContainerProps: {
                                sx: {
                                    flex: 'unset',
                                    minHeight: 0,
                                    maxHeight: 'min(560px, calc(100vh - 280px))',
                                    overflow: 'auto'
                                }
                            }
                        }}
                    />
                </Box>
            </AdmicityDialog>
        </Box>
    );
};

StudentParentInvitationsSettings.propTypes = {
    schoolId: PropTypes.number,
    embedded: PropTypes.bool,
    value: PropTypes.shape({
        disableParentInvitations: PropTypes.bool,
        parentInvitationTargetMode: PropTypes.number,
        parentInvitationYearGroupIds: PropTypes.array
    }),
    onChange: PropTypes.func
};

export default StudentParentInvitationsSettings;
